import {
  Activity,
  Calendar,
  Clock,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { utilService } from '../services/util';
import { Session } from '../types/api';

export const SessionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is source of truth for submitted search and page
  const search = searchParams.get('q') ?? undefined;
  const startDate = searchParams.get('startDate') ?? undefined;
  const endDate = searchParams.get('endDate') ?? undefined;
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const limit = 20;
  // Default checked ("valid only"); URL `valid=all` means show every session
  const validOnly = searchParams.get('valid') !== 'all';

  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') || '');

  // Sync input from URL when params change (e.g. after Clear or when returning from detail)
  useEffect(() => {
    const q = searchParams.get('q');
    setSearchInput(q || '');
  }, [searchParams]);

  // Fetch sessions with search and page from URL (only when submitted)
  const { data: sessionsData, isLoading, error, refetch } = useQuery(
    ['sessions', page, search, startDate, endDate, validOnly],
    () => apiService.getSessions({
      page,
      limit,
      search: search || undefined,
      startDate,
      endDate,
      valid: validOnly ? true : undefined,
    }),
    {
      refetchInterval: 120_000, // Refetch every 2 minutes
    }
  );

  const sessions = sessionsData?.sessions || [];

  const submitSearch = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const q = searchInput.trim();
      if (q) next.set('q', q);
      else next.delete('q');
      next.set('page', '1');
      return next;
    });
  };

  const clearSearch = () => {
    setSearchParams({});
    setSearchInput('');
  };

  const setValidOnly = (checked: boolean) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (checked) {
        next.delete('valid');
      } else {
        next.set('valid', 'all');
      }
      next.set('page', '1');
      return next;
    });
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status badge
  const getStatusBadge = (session: Session) => {
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${utilService.sessionStatusClass(session)}`}>
        {utilService.sessionStatus(session)}
      </span>
    );
  };

  // Get content summary
  const getContentSummary = (session: Session): string => {
    const hardware = session.content.filter(c => c.contentType === 'hardware').length;
    const software = session.content.filter(c => c.contentType === 'software').length;
    const services = session.content.filter(c => c.contentType === 'services').length;
    const parts: string[] = [];
    if (hardware > 0) parts.push(`${hardware} hardware`);
    if (software > 0) parts.push(`${software} software`);
    if (services > 0) parts.push(`${services} services`);
    return parts.join(', ') || 'No content';
  };

  const formatFilterDate = (dateString: string): string => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const clearDateFilter = (key: 'startDate' | 'endDate') => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(key);
      next.set('page', '1');
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Sessions</h1>
          <p className="mt-2 text-secondary-600">
            View and manage your neurofeedback training sessions
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="btn-secondary flex items-center space-x-2"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Search */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
            <input
              type="text"
              placeholder="Search sessions by ID, program, or provider..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitSearch();
              }}
              className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={submitSearch}
              className="btn-primary flex items-center space-x-2"
            >
              <Search className="h-4 w-4" />
              <span>Search</span>
            </button>
            <button
              type="button"
              onClick={clearSearch}
              className="btn-secondary flex items-center space-x-2"
            >
              <X className="h-4 w-4" />
              <span>Clear</span>
            </button>
          </div>
        </div>
        <div className="mt-3 inline-flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-secondary-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={validOnly}
              onChange={(e) => setValidOnly(e.target.checked)}
              className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Valid only</span>
          </label>
        </div>
        {(startDate || endDate) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {startDate && (
              <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-800 border border-primary-200 rounded-full px-3 py-1 text-sm">
                <span><b>Start:</b> {formatFilterDate(startDate)}</span>
                <button
                  type="button"
                  aria-label="Remove start date filter"
                  onClick={() => clearDateFilter('startDate')}
                  className="p-0.5 rounded-full hover:bg-primary-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {endDate && (
              <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-800 border border-primary-200 rounded-full px-3 py-1 text-sm">
                <span><b>End:</b> {formatFilterDate(endDate)}</span>
                <button
                  type="button"
                  aria-label="Remove end date filter"
                  onClick={() => clearDateFilter('endDate')}
                  className="p-0.5 rounded-full hover:bg-primary-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sessions List */}
      <div className="card">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-20 bg-secondary-200 rounded-lg"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-500 mb-2">Failed to load sessions</p>
            <button onClick={() => refetch()} className="btn-primary mt-4">
              Try Again
            </button>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-500 mb-2">
              {search ? 'No sessions match your search' : 'No sessions found'}
            </p>
            <p className="text-sm text-secondary-400">
              {search
                ? 'Try adjusting your search'
                : 'Start your first neurofeedback session from the Dashboard'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                      Session
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                      Start Time
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                      Duration
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                      Content
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                      Status
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-secondary-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100">
                  {sessions.map((session) => (
                    <tr
                      key={session.id}
                      className={
                        session.valid === false
                          ? 'bg-secondary-100 text-secondary-400 opacity-70 hover:bg-secondary-100 transition-colors cursor-pointer'
                          : 'hover:bg-secondary-50 transition-colors cursor-pointer'
                      }
                      onClick={() => {
                        const params = new URLSearchParams(searchParams);
                        const query = params.toString();
                        navigate(`/sessions/${session.id}${query ? `?${query}` : ''}`);
                      }}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary-100 rounded-lg">
                            <Activity className="h-4 w-4 text-primary-600" />
                          </div>
                          <div>
                            <p className="font-medium text-secondary-900">
                              {utilService.sessionName(session)}
                              {session.metadata && session.metadata.sessionNumber && session.metadata.totalSessions && (
                                <span className="font-mono font-thin text-secondary-400 ml-2">[{session.metadata.sessionNumber}/{session.metadata.totalSessions}]</span>
                              )}
                            </p>
                            {
                              session.userId
                              && typeof session.userId === 'object'
                              && (
                            <p className="text-sm text-secondary-500">
                              {(session.userId as any).fullName}
                            </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-2 text-secondary-600">
                          <Calendar className="h-4 w-4" />
                          <span className="text-sm">{formatDate(session.start)}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-2 text-secondary-600">
                          <Clock className="h-4 w-4" />
                          <span className="text-sm">{session.end ? utilService.formatDuration(session.start, session.end) : '---'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-secondary-600">
                          {getContentSummary(session)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {getStatusBadge(session)}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const params = new URLSearchParams(searchParams);
                            const query = params.toString();
                            navigate(`/sessions/${session.id}${query ? `?${query}` : ''}`);
                          }}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-secondary-200">
              <div className="text-sm text-secondary-600">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, ((page - 1) * limit) + sessions.length)}
                { sessionsData?.meta?.total && ` of ${sessionsData.meta.total}`}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    if (page <= 1) return;
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.set('page', String(page - 1));
                      return next;
                    });
                  }}
                  disabled={page === 1}
                  className="px-4 py-2 border border-secondary-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-secondary-600">
                  Page {page} {sessionsData?.meta?.totalPages && ` of ${sessionsData.meta.totalPages}`}
                </span>
                <button
                  onClick={() => {
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.set('page', String(page + 1));
                      return next;
                    });
                  }}
                  disabled={sessions.length < limit || page === sessionsData?.meta?.totalPages}
                  className="px-4 py-2 border border-secondary-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
